import React, { useMemo, useState } from 'react';
import { api } from '../../services/api';
import apiService from '../../services/api';
import DEPLOYMENT_CONFIG from '../../config/deployment';
import { latestTerminalAtFromScdList } from '../../utils/proposalComercialBoard';
import {
  getMobilidadeDetailRows,
  isMobilidadeServiceType,
} from './mobilidadeProposalFields';
import './ComercialManagement.css';

// RequestCard component styled like ContractDocument from Contratação
interface RequestCardProps {
  request: any;
  scrollableBody?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

interface CardNote {
  content?: string;
  created_at?: string;
  author_name?: string;
  created_by_name?: string;
  created_by?: string;
}

/** CRA `PUBLIC_URL`; Vite/CCC use same-origin public root (no `process` / `import.meta` in CRA types). */
function publicAssetUrl(relativePath: string): string {
  const assetPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  if (typeof process !== 'undefined' && process.env?.PUBLIC_URL != null) {
    return `${process.env.PUBLIC_URL}${assetPath}`;
  }
  return assetPath;
}

const WONDERTRADE_LOGO_FALLBACKS = [
  '/Intra-logo.png',
  '/Logo.png',
  '/LOGO-Wondertrade.png',
] as const;

function applyWondertradeLogoFallback(img: HTMLImageElement): void {
  const step = Number(img.dataset.fallbackStep || '0');
  const next = WONDERTRADE_LOGO_FALLBACKS[step];
  if (!next) return;
  img.dataset.fallbackStep = String(step + 1);
  img.src = publicAssetUrl(next);
}

const RequestCard: React.FC<RequestCardProps> = ({ request, scrollableBody, expanded, onToggleExpand }) => {
  // Collapsible section states
  const [completionExpanded, setCompletionExpanded] = useState(false);
  const [cancellationExpanded, setCancellationExpanded] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const isCollapsible = Boolean(scrollableBody);
  const isControlledExpand = typeof expanded === 'boolean';
  const [localExpanded, setLocalExpanded] = useState<boolean>(!isCollapsible);
  const isExpanded = isControlledExpand ? Boolean(expanded) : localExpanded;

  const handleToggleExpand = () => {
    if (isControlledExpand) {
      onToggleExpand?.();
      return;
    }
    setLocalExpanded((prev) => !prev);
  };
  
  // Extract proposal data - matching "Ver Detalhes" modal structure
  // ONLY use data from the proposal request form_data, NOT from lead/marcação
  const formData = request.proposal_request?.form_data || request.proposal_request?.form_answers || request.form_answers || request.form_data || {};
  const empresa = formData.empresa || {};
  const equipaInterna = formData.equipaInterna || {};
  const serviceDetails = formData.serviceDetails || {};
  const fileReferences = formData.fileReferences || {};
  const servicos = formData.servicos || [];
  
  // Extract BackOffice completion and cancellation data
  const proposalClaim = request.proposal_claim || null;
  const serviceCompletionData = Array.isArray(proposalClaim?.service_completion_data)
    ? proposalClaim.service_completion_data
    : [];
  const completionNotes = request.completion_notes || proposalClaim?.completion_notes || null;
  const completedAt =
    request.completed_at ||
    proposalClaim?.completed_at ||
    (serviceCompletionData.length > 0 ? latestTerminalAtFromScdList(serviceCompletionData) : null);
  const backofficeUser = request.backoffice_user || null;
  const cancelledByUsername = request.cancelled_by_username || null;
  const cancelledAt = request.cancelled_at || null;
  const cancellationFeedback = request.cancellation_feedback || null;
  
  // Get produtos/servicos names - ONLY from servicos array
  const getProdutosText = () => {
    const produtos: string[] = [];
    if (Array.isArray(servicos)) {
      servicos.forEach((s: any) => {
        if (typeof s === 'object' && s?.service) {
          const serviceNames: Record<string, string> = {
            'bairro-solar': 'Bairro Solar',
            'pap': 'Pay AS Produced (PAP)',
            'transacional': 'Transacional',
            'luz': 'Luz',
            'gas': 'Gás',
            'mobilidade': 'Mobilidade Elétrica',
            'outro': 'Outro'
          };
          const displayName = serviceNames[s.service] || s.service;
          produtos.push(displayName);
        } else if (typeof s === 'string') {
          produtos.push(s);
        }
      });
    }
    return Array.from(new Set(produtos)).join(', ') || 'N/A';
  };
  
  // Extract proposal ID
  const idProposta = request.id || formData.propostaId || 'N/A';
  const idPropostaDisplay = String(idProposta).startsWith('PRP-') ? idProposta : `PRP-${idProposta}`;
  
  // Get empresa name for title
  const empresaName = empresa.nomeEmpresa || 'N/A';
  const servicosText = getProdutosText();
  const cardTitle = `${empresaName} - ${servicosText}`;
  
  // Get status tag from kanbanSection (Comercial: BackOffice "Disponível" → Por Iniciar)
  const SECTION_LABELS: Record<string, string> = {
    available: 'Por Iniciar',
    roadtrip: 'RoadTrip',
    op_criada: 'OP Criada',
    in_progress: 'Em Progresso',
    proposta_aprovacao: 'Proposta em Aprovação',
    exclusividade_pendente: 'Exclusividade',
    pendente_gestor_edp: 'Pendente Gestor EDP (Erro Sistema)',
    pendente_comercial: 'Pendente Comercial',
    completed: 'Concluído / Cancelado',
  };
  const getStatusTag = () => {
    const section = request.kanbanSection || 'available';
    const text = SECTION_LABELS[section] || section;
    const boardStatus = request.boardStatus || 'available';
    if (section === 'completed') {
      return {
        text: boardStatus === 'cancelled' ? 'Cancelado' : 'Concluído',
        color: boardStatus === 'cancelled' ? '#ef4444' : '#10b981',
        bgColor: boardStatus === 'cancelled' ? '#fee2e2' : '#d1fae5'
      };
    }
    const colors: Record<string, { color: string; bgColor: string }> = {
      available: { color: '#3b82f6', bgColor: '#dbeafe' },
      roadtrip: { color: '#7c3aed', bgColor: '#ede9fe' },
      op_criada: { color: '#6b7280', bgColor: '#f3f4f6' },
      in_progress: { color: '#f59e0b', bgColor: '#fef3c7' },
      proposta_aprovacao: { color: '#0ea5e9', bgColor: '#e0f2fe' },
      exclusividade_pendente: { color: '#8b5cf6', bgColor: '#f5f3ff' },
      pendente_gestor_edp: { color: '#c2410c', bgColor: '#fff7ed' },
      pendente_comercial: { color: '#0e7490', bgColor: '#ecfeff' },
    };
    const style = colors[section] || colors.available;
    return { text, ...style };
  };

  // Card tags from quadro de propostas (Exclusividade, etc.) – show Exclusividade when present
  const cardTags: { type: string; ate?: string; value?: string }[] = Array.isArray(request.card_tags) ? request.card_tags : [];
  const exclusividadeTag = cardTags.find((t: any) => t.type === 'exclusividade' && t.ate);
  const isRefazer =
    request.is_refazer === true ||
    formData.isRefazer === true ||
    cardTags.some((t: any) => t.type === 'refazer');
  const exclusividadeDaysLeft = (() => {
    if (!exclusividadeTag?.ate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ateDate = new Date(exclusividadeTag.ate);
    ateDate.setHours(0, 0, 0, 0);
    const diffTime = ateDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  })();
  
  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  // Format date and time helper
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };
  
  // Format file size helper
  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  
  const statusTag = getStatusTag();
  const resolvedLeadId = Number(
    request?.lead_id ??
    formData?.lead_id ??
    formData?.leadId ??
    request?.proposal_request?.lead_id ??
    0
  ) || null;
  
  // Format timestamp for display
  const formatTimestamp = () => {
    const submittedDate = request.submitted_at || request.created_at || request.updated_at;
    if (!submittedDate) return 'N/A';
    
    const date = new Date(submittedDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };
  
  const timestamp = formatTimestamp();
  const cardNotes: CardNote[] = useMemo(() => {
    const sources = [
      request?.proposal_request?.form_data?.card_notes,
      request?.proposal_request?.form_answers?.card_notes,
      request?.card_notes,
      request?.form_data?.card_notes,
      request?.form_answers?.card_notes,
      formData?.card_notes
    ];
    for (const source of sources) {
      if (Array.isArray(source)) {
        return [...source].reverse(); // newest first
      }
    }
    return [];
  }, [request, formData]);
  
  // Extract files from serviceDetails and fileReferences
  const extractFilesFromServiceDetails = (details: any, serviceId: string, fileRefs: any): any[] => {
    const files: any[] = [];
    const seenFileIds = new Set<string | number>(); // Track files by lead_file_id to prevent duplicates
    const seenFileNames = new Set<string>(); // Track files by name to prevent duplicates across different fields
    
    // Helper to check if file already exists (checks by ID, name, or name+field)
    const isFileAlreadyAdded = (fileObj: any): boolean => {
      // Check by lead_file_id first (most reliable)
      if (fileObj.lead_file_id && seenFileIds.has(fileObj.lead_file_id)) {
        return true;
      }
      // Check by file name (to catch duplicates across different field names)
      const fileName = fileObj.name || fileObj.file_name || fileObj.filename;
      if (fileName && seenFileNames.has(fileName)) {
        return true;
      }
      // Check by name + field combination (fallback)
      return files.some(f => {
        const fName = f.name || f.file_name || f.filename;
        return fName === fileName && f.field === fileObj.field;
      });
    };
    
    // Helper to add file if not duplicate
    const addFileIfUnique = (fileObj: any) => {
      if (!isFileAlreadyAdded(fileObj)) {
        files.push(fileObj);
        if (fileObj.lead_file_id) {
          seenFileIds.add(fileObj.lead_file_id);
        }
        const fileName = fileObj.name || fileObj.file_name || fileObj.filename;
        if (fileName) {
          seenFileNames.add(fileName);
        }
      }
    };
    
    // PRIMARY SOURCE: Check fileReferences for this service (source of truth)
    if (fileRefs && typeof fileRefs === 'object' && fileRefs[serviceId]) {
      const serviceFileRefs = fileRefs[serviceId];
      Object.entries(serviceFileRefs).forEach(([field, fileData]: [string, any]) => {
        if (fileData && typeof fileData === 'object') {
          const fileName = fileData.name || fileData.file_name || fileData.filename || fileData.originalName || fileData.original_name;
          if (fileName) {
            addFileIfUnique({
              name: fileName,
              id: fileData.id || fileData.file_id,
              size: fileData.size || fileData.file_size,
              type: fileData.type || fileData.file_type || fileData.content_type || fileData.mimeType || fileData.mime_type,
              url: fileData.url || fileData.file_url || fileData.download_url,
              public_id: fileData.public_id || fileData.publicId || fileData.cloudinary_id,
              lead_file_id: fileData.lead_file_id || fileData.leadFileId,
              lead_id: fileData.lead_id || fileData.leadId,
              data: fileData.data || fileData.file_data,
              field: field
            });
          }
        } else if (fileData && typeof fileData === 'string') {
          // If fileData is a string (could be a URL or public_id), try to extract name from field
          const fileName = field.includes('_name') ? fileData : `${field}_${serviceId}`;
          addFileIfUnique({
            name: fileName,
            url: field.includes('_url') ? fileData : undefined,
            public_id: !field.includes('_url') && !field.includes('_name') ? fileData : undefined,
            field: field
          });
        }
      });
    }
    
    // SECONDARY SOURCE: Check for files array in details (only if not already in fileReferences)
    if (details.files && Array.isArray(details.files)) {
      details.files.forEach((file: any) => {
        if (file && typeof file === 'object') {
          const fileName = file.name || file.file_name || file.filename || file.originalName || file.original_name;
          if (fileName) {
            addFileIfUnique({
              name: fileName,
              id: file.id || file.file_id,
              size: file.size || file.file_size,
              type: file.type || file.file_type || file.content_type || file.mimeType || file.mime_type,
              url: file.url || file.file_url || file.download_url,
              public_id: file.public_id || file.publicId || file.cloudinary_id,
              lead_file_id: file.lead_file_id || file.leadFileId,
              lead_id: file.lead_id || file.leadId,
              data: file.data || file.file_data,
              field: file.field || 'unknown'
            });
          }
        }
      });
    }
    
    // TERTIARY SOURCE: Check for individual file fields in serviceDetails (fatura_name, printTelhado_name, etc.)
    // ONLY if not already found in fileReferences AND has a valid lead_file_id or non-empty name
    const fileFields = ['fatura', 'printTelhado', 'declaracaoInicioAtividade', 'inicioAtividade'];
    fileFields.forEach((field) => {
      const fileNameField = `${field}_name`;
      const fileUrlField = `${field}_url`;
      const filePublicIdField = `${field}_public_id`;
      const fileLeadFileIdField = `${field}_lead_file_id`;
      
      // Only add if:
      // 1. File name exists and is not empty
      // 2. File is NOT already in fileReferences (to avoid duplication)
      // 3. File name is not already seen (to prevent duplicates across fields)
      const fileName = details[fileNameField];
      const leadFileId = details[fileLeadFileIdField];
      
      if (fileName && fileName.trim() !== '') {
        // Check if this file is already in fileReferences
        const alreadyInFileRefs = fileRefs && fileRefs[serviceId] && fileRefs[serviceId][field];
        
        // Check if this file name was already added (even from a different field)
        const alreadyAddedByName = seenFileNames.has(fileName);
        
        if (!alreadyInFileRefs && !alreadyAddedByName) {
          addFileIfUnique({
            name: fileName,
            id: details[`${field}_id`] || details[`${field}_file_id`],
            url: details[fileUrlField] || details[field],
            public_id: details[filePublicIdField],
            lead_file_id: leadFileId,
            field: field
          });
        }
      }
    });
    
    return files;
  };
  
  const allFiles: any[] = [];
  Object.entries(serviceDetails).forEach(([service, details]: [string, any]) => {
    if (details && typeof details === 'object') {
      const serviceFiles = extractFilesFromServiceDetails(details, service, fileReferences);
      serviceFiles.forEach((file: any) => {
        // Ensure we have a valid file name - if not, try to infer from type or use a default
        if (!file.name || file.name === 'unnamed image.png' || file.name.includes('unnamed')) {
          // Try to get name from other properties
          const inferredName = file.originalName || file.original_name || file.fileName || file.file_name || 
                              (file.type && file.type.includes('image') ? 'imagem.png' : 'documento.pdf');
          file.name = inferredName;
        }
        allFiles.push({ ...file, service });
      });
    }
  });
  
  // Handle file download
  const handleFileDownload = async (file: any, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    
    if (!file) {
      alert('Erro: Ficheiro não encontrado');
      return;
    }
    
    try {
      // If file has a lead_file_id, use lead file download endpoint
      if (file.lead_file_id || file.leadFileId || file.file_id || file.id) {
        const leadId = file.lead_id || formData.lead_id || request.lead_id || resolvedLeadId;
        const fileId = file.lead_file_id || file.leadFileId || file.file_id || file.id;
        
        if (leadId && fileId) {
          try {
            const response = await api.get(`/api/leads/${leadId}/files/${fileId}/download`, {
              responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name || file.file_name || 'file';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, 100);
            return;
          } catch (error) {
            console.error('[ComercialManagement] Error downloading file from API:', error);
          }
        }

        // Fallback for records that only have file ID (no lead_id persisted)
        if (fileId) {
          try {
            const response = await api.get(`/api/leads/files/${fileId}/download`, {
              responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name || file.file_name || 'file';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, 100);
            return;
          } catch (error) {
            console.error('[ComercialManagement] Error downloading by file ID fallback:', error);
          }
        }
      }
      
      // If file has a public_id, use chat download endpoint
      if (file.public_id || file.publicId || file.cloudinary_id) {
        const publicId = file.public_id || file.publicId || file.cloudinary_id;
        
        try {
          const response = await api.get(`/api/chat/download/${encodeURIComponent(publicId)}`);
          
          if (response.data && response.data.success && response.data.download_url) {
            const link = document.createElement('a');
            link.href = response.data.download_url;
            link.download = file.name || file.file_name || 'file';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
            }, 100);
            return;
          }
        } catch (apiError) {
          // Continue to next download method
        }
      }
      
      // If file has a URL, download directly
      if (file.url || file.file_url || file.download_url) {
        const url = file.url || file.file_url || file.download_url;
        const backendUrl = DEPLOYMENT_CONFIG.getBackendUrl();
        const fullUrl = url.startsWith('http') 
          ? url 
          : `${backendUrl}${url.startsWith('/') ? '' : '/'}${url}`;
        
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = file.name || file.file_name || 'file';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
        return;
      }
      
      // If file has data (base64), download it
      if (file.data || file.file_data) {
        const data = file.data || file.file_data;
        
        // Convert base64 to blob if needed
        let blobUrl = data;
        if (data.startsWith('data:')) {
          // Already a data URL
          blobUrl = data;
        } else {
          // Assume it's base64, try to create blob
          try {
            const byteCharacters = atob(data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: file.type || 'application/octet-stream' });
            blobUrl = URL.createObjectURL(blob);
          } catch (e) {
            blobUrl = data; // Fallback to direct use
          }
        }
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.name || file.file_name || 'file';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          if (blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrl);
          }
        }, 100);
        return;
      }
      
      // Fallback: Try to fetch file by field_name and lead_id if we have those but no lead_file_id
      if (file.field && (file.lead_id || formData.lead_id || request.lead_id || resolvedLeadId)) {
        const leadId = file.lead_id || formData.lead_id || request.lead_id || resolvedLeadId;
        const fieldName = file.field;
        
        try {
          const filesResponse = await apiService.getLeadFiles(leadId, { 
            stage: 'proposta', 
            field_name: fieldName 
          });
          
          if (filesResponse.success && filesResponse.files && filesResponse.files.length > 0) {
            // Use the first matching file
            const foundFile = filesResponse.files[0];
            const fileId = foundFile.id || foundFile.lead_file_id;
            
            if (fileId && leadId) {
              try {
                const response = await api.get(`/api/leads/${leadId}/files/${fileId}/download`, {
                  responseType: 'blob'
                });
                const blob = new Blob([response.data], { type: file.type || 'application/octet-stream' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.name || foundFile.file_name || file.file_name || 'file';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }, 100);
                return;
              } catch (error) {
                console.error('[ComercialManagement] Error downloading file:', error);
              }
            }
          }
        } catch (fetchError) {
          console.error('Error fetching file by field_name:', fetchError);
          // Continue to show error message below
        }
      }
      
      alert(`Não foi possível descarregar o ficheiro "${file.name || 'desconhecido'}". Informações do ficheiro não disponíveis.`);
    } catch (error) {
      alert(`Erro ao descarregar o ficheiro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };
  
  // Handle download all files
  const handleDownloadAll = async (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (allFiles.length === 0) {
      alert('Não há ficheiros para descarregar');
      return;
    }
    
    for (let i = 0; i < allFiles.length; i++) {
      const file = allFiles[i];
      // Add a small delay between downloads to avoid browser blocking
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      await handleFileDownload(file);
    }
  };
  
  const getFileIconClass = (type: string, name: string): string => {
    if (type.includes('pdf') || name.toLowerCase().endsWith('.pdf')) return 'file-icon-pdf';
    if (type.includes('image') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name)) return 'file-icon-image';
    if (type.includes('word') || name.toLowerCase().endsWith('.doc') || name.toLowerCase().endsWith('.docx')) return 'file-icon-word';
    if (type.includes('excel') || name.toLowerCase().endsWith('.xls') || name.toLowerCase().endsWith('.xlsx')) return 'file-icon-excel';
    if (type.includes('zip') || /\.(zip|rar|7z|tar|gz)$/i.test(name)) return 'file-icon-zip';
    return 'file-icon-generic';
  };
  
  // Determine CSS classes based on board status
  const boardStatus = request.boardStatus || 'available';
  const kanbanSection = request.kanbanSection || 'available';
  const isInProgress = boardStatus === 'claimed' || boardStatus === 'in_progress';
  // Align with the status tag logic:
  // when the card is in "completed" kanban section, it shows "Concluído" unless explicitly cancelled.
  const isCancelled = kanbanSection === 'completed' && boardStatus === 'cancelled';
  const isCompleted = kanbanSection === 'completed' && !isCancelled;
  const showCompletionBlock = serviceCompletionData.length > 0 && boardStatus !== 'cancelled';
  const completionSectionTitle = isCompleted ? 'Concluído' : 'Respostas do BackOffice';

  const documentClasses = [
    'contract-document',
    'proposal-card',
    scrollableBody ? 'minhas-propostas-request-card' : '',
    isInProgress ? 'in-progress' : '',
    isCompleted && !isCancelled ? 'completed' : '',
    isCancelled ? 'cancelled' : ''
  ].filter(Boolean).join(' ');
  
  return (
    <div className={documentClasses}>
      <div
        className={`document-header${scrollableBody ? ' minhas-propostas-card-header' : ''}`}
        style={{ position: 'relative', cursor: isCollapsible ? 'pointer' : 'default' }}
        onClick={isCollapsible ? handleToggleExpand : undefined}
      >
        {/* Read-only BackOffice notes icon for Comercial users */}
        <div className="proposal-card-notes-trigger">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowNotesModal(true);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            title="Ver notas do BackOffice"
            aria-label="Ver notas do BackOffice"
            className="proposal-card-notes-btn"
          >
            <img
              src={publicAssetUrl('/sticky-note.png')}
              alt="Notas"
              className="proposal-card-notes-img"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallback) return;
                img.dataset.fallback = '1';
                img.style.display = 'none';
                const parent = img.parentElement;
                if (parent && !parent.querySelector('.proposal-card-notes-fallback')) {
                  const span = document.createElement('span');
                  span.className = 'proposal-card-notes-fallback';
                  span.setAttribute('aria-hidden', 'true');
                  span.textContent = '📝';
                  span.style.fontSize = '16px';
                  span.style.lineHeight = '1';
                  parent.insertBefore(span, img);
                }
              }}
            />
            {cardNotes.length > 0 && (
              <span className="proposal-card-notes-badge">
                {cardNotes.length > 9 ? '9+' : cardNotes.length}
              </span>
            )}
          </button>
        </div>

        {/* Timestamp stamp and Status Tag at the bottom right of the header */}
        <div className="proposal-card-header-meta">
          <span className="proposal-card-header-ts">{timestamp}</span>
          <span
            className="proposal-card-header-status"
            style={{
              color: statusTag.color,
              backgroundColor: statusTag.bgColor,
              borderColor: statusTag.color,
            }}
          >
            {statusTag.text}
          </span>
          {/* Exclusividade tag (from quadro de propostas) */}
          {isRefazer && (
            <span style={{
              padding: '4px 6px',
              fontSize: '10px',
              fontWeight: 600,
              borderRadius: '6px',
              backgroundColor: '#7c3aed',
              color: '#fff',
            }}>
              Refazer
            </span>
          )}
          {exclusividadeTag && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{
                padding: '4px 6px',
                fontSize: '10px',
                fontWeight: 600,
                borderRadius: '6px',
                backgroundColor: '#facc15',
                color: '#1c1917',
              }}>
                Exclusividade até {formatDate(exclusividadeTag.ate!)}
              </span>
              {exclusividadeDaysLeft !== null && (
                <span style={{
                  padding: '2px 8px',
                  backgroundColor: '#facc15',
                  color: '#1c1917',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '4px',
                  letterSpacing: '0.5px',
                }}>
                  {exclusividadeDaysLeft <= 0 ? 'esgotado' : exclusividadeDaysLeft === 1 ? '1 dia' : `${exclusividadeDaysLeft} dias`}
                </span>
              )}
            </span>
          )}
        </div>
        
        <div className="document-logo">
          <img
            src={publicAssetUrl('/Intra-logo.png')}
            alt="Wondertrade Logo"
            onError={(e) => applyWondertradeLogoFallback(e.currentTarget)}
          />
        </div>
        <div className="document-title">
          <h2>{cardTitle}</h2>
          <p className="document-id">ID: {idPropostaDisplay}</p>
          {request.is_shared_with_me ? (
            <span className="proposal-card-shared-badge">Partilhado comigo</span>
          ) : null}
        </div>
      </div>

      {isExpanded && (
      <div className="document-body">
        <div className="document-body-scroll" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Completion/Cancellation first (Minhas Propostas cards) */}
        {/* Completion / isolado answers from BackOffice (full concluído or partial) */}
        {showCompletionBlock && (
          <div className="document-text-block proposal-status-section proposal-status-section-completed" style={{ marginTop: '20px', order: -2 }}>
            <div
              className="proposal-status-header proposal-status-header-completed"
              onClick={() => setCompletionExpanded(!completionExpanded)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '12px',
                background: isCompleted ? '#f0fdf4' : '#fffbeb',
                borderRadius: '8px',
                border: isCompleted ? '1px solid #10b981' : '1px solid #f59e0b',
                marginBottom: completionExpanded ? '12px' : '0'
              }}
            >
              <h3 style={{ 
                color: isCompleted ? '#10b981' : '#d97706', 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{isCompleted ? '✓' : '◆'}</span>
                <span>{completionSectionTitle}</span>
              </h3>
              <span style={{ 
                fontSize: '18px', 
                color: isCompleted ? '#10b981' : '#d97706',
                transform: completionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}>
                ▼
              </span>
            </div>
            
            {completionExpanded && (
              <div className="proposal-status-content proposal-status-content-completed" style={{ 
                background: '#f8f9fa', 
                padding: '20px', 
                borderRadius: '12px',
                marginTop: '12px'
              }}>
                {/* Completion Info - When */}
                {completedAt && (
                  <div className="proposal-status-meta-card" style={{ marginBottom: '20px', padding: '15px', background: 'white', borderRadius: '8px', borderLeft: `4px solid ${isCompleted ? '#10b981' : '#f59e0b'}` }}>
                    <p style={{ margin: 0, fontSize: '13px' }}>
                      <strong>{isCompleted ? 'Concluído em:' : 'Última resposta:'}</strong> {formatDateTime(completedAt)}
                    </p>
                  </div>
                )}
                
                {/* Service Completion Data */}
                {serviceCompletionData.map((completionData: any, index: number) => {
                  const instanceId = completionData.instanceId;
                  const serviceData = servicos.find((s: any) => (s.instanceId || `servico-${servicos.indexOf(s)}`) === instanceId);
                  const serviceType = serviceData?.service || completionData.service || 'unknown';
                  
                  const serviceNames: Record<string, string> = {
                    'bairro-solar': 'Bairro Solar',
                    'pap': 'Pay AS Produced (PAP)',
                    'transacional': 'Transacional',
                    'luz': 'Luz',
                    'gas': 'Gás',
                    'eletricidade': 'Eletricidade',
                    'mobilidade': 'Mobilidade Elétrica',
                    'outro': 'Outro'
                  };
                  const serviceDisplayName = serviceNames[serviceType] || serviceType;
                  const instanceNumber = serviceData?.instanceNumber || index + 1;
                  
                  return (
                    <div className="proposal-status-detail-card" key={instanceId || index} style={{ marginBottom: '20px', padding: '15px', background: 'white', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                      <h4 style={{ color: 'var(--wondertrade-red)', marginBottom: '15px', fontSize: '16px' }}>
                        Serviço {instanceNumber}: {serviceDisplayName}
                      </h4>
                      
                      {/* Email Content */}
                      {completionData.emailData && Object.keys(completionData.emailData).length > 0 && (
                        <div style={{ marginBottom: '15px' }}>
                          <h5 style={{ color: '#374151', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>Email Enviado:</h5>
                          <div className="proposal-status-email-body" style={{ 
                            background: 'white', 
                            padding: '15px', 
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            whiteSpace: 'pre-line',
                            lineHeight: '1.6',
                            fontSize: '13px'
                          }}>
                            {(() => {
                              // Generate email body based on service type
                              const emailData = completionData.emailData;
                              const serviceType = completionData.service || serviceData?.service || 'unknown';
                              
                              // For luz, gas, eletricidade services
                              if (['luz', 'gas', 'eletricidade'].includes(serviceType)) {
                                const empresaName = empresa.nomeEmpresa || 'N/A';
                                const numeroProposta = emailData.numeroProposta || '[PREENCHER]';
                                const dataValidade = emailData.dataValidade ? formatDate(emailData.dataValidade) : '[PREENCHER]';
                                const dataEnvio = emailData.dataEnvio ? formatDate(emailData.dataEnvio) : '[PREENCHER]';
                                const horaEnvio = emailData.horaEnvio || '[PREENCHER]';
                                
                                return `Bom dia,

Segue o contrato: ${empresaName}

Nº Proposta - ${numeroProposta}
Data Validade - ${dataValidade}

Data de envio Proposta assinada ${dataEnvio} até às ${horaEnvio}

Obrigada.`;
                              }
                              
                              // For bairro-solar, pap services
                              if (['bairro-solar', 'pap'].includes(serviceType)) {
                                const empresaName = empresa.nomeEmpresa || 'N/A';
                                const propostaId = emailData.propostaId || emailData.idPropostaS2C || '[PREENCHER]';
                                const investimento = emailData.investimento ? `${emailData.investimento} €` : '[PREENCHER]';
                                const validade = emailData.validade || emailData.dataValidade ? formatDate(emailData.validade || emailData.dataValidade) : '[PREENCHER]';
                                
                                return `Bom dia,

Segue a proposta para: ${empresaName}

ID da Proposta (S2C): ${propostaId}
Investimento: ${investimento}
Validade: ${validade}

Obrigada.`;
                              }
                              
                              // For other services or if email body is stored directly
                              if (emailData.emailBody || emailData.body || emailData.content) {
                                return emailData.emailBody || emailData.body || emailData.content;
                              }
                              
                              // Fallback: show all email data as formatted text
                              return Object.entries(emailData)
                                .filter(([key]) => !['emailBody', 'body', 'content'].includes(key))
                                .map(([key, value]) => {
                                  const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                                  return `${displayKey}: ${value || 'N/A'}`;
                                })
                                .join('\n');
                            })()}
                          </div>
                        </div>
                      )}
                      
                      {/* Completion Files */}
                      {completionData.files && completionData.files.length > 0 && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
                          <h5 style={{ color: '#374151', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
                            Ficheiros Carregados ({completionData.files.length}):
                          </h5>
                          <div className="proposal-status-files-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {completionData.files.map((file: any, fileIndex: number) => {
                              const fileName = file.name || file.file_name || file.fileName || `Ficheiro ${fileIndex + 1}`;
                              const fileSize = file.size || file.file_size || file.fileSize;
                              
                              return (
                                <div
                                  className="proposal-status-file-chip"
                                  key={fileIndex} 
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    background: '#e5e7eb',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    border: '1px solid #d1d5db',
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                  onClick={(e) => {
                                    // Prevent click from bubbling up to parent collapsible section
                                    e.stopPropagation();
                                  }}
                                >
                                  <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {fileName}
                                  </span>
                                  {fileSize && (
                                    <span style={{ color: '#666', fontSize: '11px' }}>
                                      ({formatFileSize(fileSize)})
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      
                                      // Ensure file has all necessary properties for download
                                      const fileForDownload = {
                                        ...file,
                                        name: fileName,
                                        file_name: fileName,
                                        size: fileSize,
                                        file_size: fileSize,
                                        lead_file_id: file.lead_file_id || file.leadFileId,
                                        lead_id: file.lead_id || formData.lead_id || request.lead_id,
                                        public_id: file.public_id || file.publicId || file.cloudinary_id,
                                        url: file.url || file.file_url || file.download_url,
                                        data: file.data || file.file_data,
                                        type: file.type || file.file_type || file.content_type
                                      };
                                      
                                      try {
                                        await handleFileDownload(fileForDownload, e);
                                      } catch (error) {
                                        console.error('Error in download button handler:', error);
                                        alert(`Erro ao descarregar: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Prevent any parent handlers from interfering
                                      e.stopPropagation();
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      background: '#10b981',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      position: 'relative',
                                      zIndex: 100,
                                      pointerEvents: 'auto',
                                      flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#059669';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#10b981';
                                    }}
                                  >
                                    Descarregar
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Completion Notes */}
                {completionNotes && (
                  <div className="proposal-status-note-card" style={{ marginTop: '15px', padding: '15px', background: 'white', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                    <h5 style={{ color: '#374151', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>Notas de Conclusão:</h5>
                    <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0, fontSize: '13px' }}>{completionNotes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cancellation Data Section - Collapsible */}
        {isCancelled && (
          <div className="document-text-block proposal-status-section proposal-status-section-cancelled" style={{ marginTop: '20px', order: -1 }}>
            <div
              className="proposal-status-header proposal-status-header-cancelled"
              onClick={() => setCancellationExpanded(!cancellationExpanded)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '12px',
                background: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #ef4444',
                marginBottom: cancellationExpanded ? '12px' : '0'
              }}
            >
              <h3 style={{ 
                color: '#ef4444', 
                margin: 0, 
                fontSize: '16px', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>✕</span>
                <span>Cancelado</span>
              </h3>
              <span style={{ 
                fontSize: '18px', 
                color: '#ef4444',
                transform: cancellationExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}>
                ▼
              </span>
            </div>
            
            {cancellationExpanded && (
              <div className="proposal-status-content proposal-status-content-cancelled" style={{ 
                background: '#fee2e2', 
                padding: '20px', 
                borderRadius: '12px',
                marginTop: '12px',
                border: '1px solid #ef4444'
              }}>
                {cancelledAt && (
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: 0, fontSize: '13px' }}>
                      <strong>Cancelado em:</strong> {formatDate(cancelledAt)}
                    </p>
                  </div>
                )}
                
                {cancellationFeedback && (
                  <div style={{ marginTop: '15px' }}>
                    <h5 style={{ color: '#374151', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>
                      Motivo do Cancelamento:
                    </h5>
                    <div style={{ 
                      background: 'white', 
                      padding: '15px', 
                      borderRadius: '8px', 
                      marginTop: '10px',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      lineHeight: 1.6,
                      border: '1px solid #fca5a5',
                      fontSize: '13px'
                    }}>
                      {cancellationFeedback}
                    </div>
                  </div>
                )}
                
                {!cancellationFeedback && (
                  <div style={{ 
                    background: 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    marginTop: '10px',
                    color: '#666',
                    fontStyle: 'italic',
                    border: '1px solid #fca5a5',
                    fontSize: '13px'
                  }}>
                    Nenhum feedback de cancelamento disponível.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Informações Gerais */}
        <div className="document-text-block">
          <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: 10, fontSize: '16px', fontWeight: '600' }}>Informações Gerais</h3>
          <div className="text-block-line"><strong>ID:</strong> {idPropostaDisplay}</div>
          <div className="text-block-line"><strong>Cliente:</strong> {empresa.nomeRepresentante || empresa.contactoRepresentante || 'N/A'}</div>
          <div className="text-block-line"><strong>Empresa:</strong> {empresa.nomeEmpresa || 'N/A'}</div>
          {formData.equipaInterna?.tipoPedido === 'roadtrip' && (
            <div className="text-block-line"><strong>Tipo:</strong> <span style={{ marginLeft: 8, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontSize: '12px' }}>🚗 RoadTrip</span></div>
          )}
        </div>
        
        {/* Informações da Empresa */}
        <div className="document-text-block" style={{ marginTop: '20px' }}>
          <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: 10, fontSize: '16px', fontWeight: '600' }}>Informações da Empresa</h3>
          {empresa.nomeEmpresa && <div className="text-block-line"><strong>Nome da Empresa:</strong> {empresa.nomeEmpresa}</div>}
          {empresa.nifEmpresa && <div className="text-block-line"><strong>NIF:</strong> {empresa.nifEmpresa}</div>}
          {empresa.regiao && <div className="text-block-line"><strong>Localidade:</strong> {empresa.regiao}</div>}
          {empresa.cae && <div className="text-block-line"><strong>CAE:</strong> {empresa.cae}</div>}
          {empresa.moradaCorrespondencia && <div className="text-block-line"><strong>Morada de Correspondência:</strong> {empresa.moradaCorrespondencia}</div>}
          {empresa.emailEmpresa && <div className="text-block-line"><strong>Email da Empresa:</strong> {empresa.emailEmpresa}</div>}
          {empresa.nomeRepresentante && <div className="text-block-line"><strong>Nome do Representante:</strong> {empresa.nomeRepresentante}</div>}
          {empresa.contactoRepresentante && <div className="text-block-line"><strong>Contacto do Representante:</strong> {empresa.contactoRepresentante}</div>}
          {empresa.emailRepresentante && <div className="text-block-line"><strong>Email do Representante:</strong> {empresa.emailRepresentante}</div>}
        </div>
        
        {/* Serviços */}
        <div className="document-text-block" style={{ marginTop: '20px' }}>
          <h3 style={{ color: 'var(--wondertrade-red)', marginBottom: 10, fontSize: '16px', fontWeight: '600' }}>Serviços</h3>
          <div className="text-block-line"><strong>Serviços Selecionados:</strong> {getProdutosText()}</div>
          
          {/* Show service details for each service */}
          {Array.isArray(servicos) && servicos.length > 0 && servicos.map((servico: any, servicoIndex: number) => {
            if (typeof servico !== 'object') return null;
            
            const instanceId = servico.instanceId || `servico-${servicoIndex}`;
            const serviceName = servico.service || 'unknown';
            const instanceNumber = servico.instanceNumber || null;
            const details = serviceDetails[instanceId] || {};
            
            const serviceNames: Record<string, string> = {
              'bairro-solar': 'Bairro Solar',
              'pap': 'Pay AS Produced (PAP)',
              'transacional': 'Transacional',
              'luz': 'Luz',
              'gas': 'Gás',
              'mobilidade': 'Mobilidade Elétrica',
              'outro': 'Outro'
            };
            const baseDisplayName = serviceNames[serviceName] || serviceName;
            const displayName = instanceNumber ? `${baseDisplayName} - ${instanceNumber}` : baseDisplayName;
            
            const serviceFiles = extractFilesFromServiceDetails(details, instanceId, fileReferences);
            const hasData = Object.entries(details).some(([key, value]) => {
              if (key === 'files') return false;
              if (value === null || value === undefined || value === '') return false;
              return true;
            });
            
            if (!hasData && serviceFiles.length === 0) return null;
            
            return (
              <div key={instanceId} style={{ marginTop: 15, padding: 12, background: '#f8f9fa', borderRadius: 8, borderLeft: '4px solid var(--wondertrade-red)' }}>
                <div style={{ fontWeight: '600', color: 'var(--wondertrade-red)', marginBottom: 8 }}>{displayName.toUpperCase()}</div>
                {(details.moradaFornecimento || empresa.moradaFornecimento) && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>Morada de Fornecimento:</strong> {details.moradaFornecimento || empresa.moradaFornecimento}</div>}
                {(serviceName === 'bairro-solar' || serviceName === 'pap' || serviceName === 'luz' || serviceName === 'transacional') && (
                  <>
                    {details.cpe && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>CPE:</strong> {details.cpe}</div>}
                    {details.potencia && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>Potência:</strong> {details.potencia === 'outra' ? (details.potenciaOutra || details.potencia) : details.potencia.toUpperCase()}</div>}
                    {details.observacoes && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>Observações:</strong> {details.observacoes}</div>}
                  </>
                )}
                {serviceName === 'gas' && (
                  <>
                    {details.cui && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>CUI:</strong> {details.cui}</div>}
                    {details.escalao && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>Escalão:</strong> Escalão {details.escalao}</div>}
                    {details.observacoes && <div className="text-block-line" style={{ fontSize: '13px' }}><strong>Observações:</strong> {details.observacoes}</div>}
                  </>
                )}
                {isMobilidadeServiceType(serviceName) && (
                  <>
                    {getMobilidadeDetailRows(details).map((row) => (
                      <div key={row.key} className="text-block-line" style={{ fontSize: '13px' }}>
                        <strong>{row.label}:</strong> {row.value}
                      </div>
                    ))}
                  </>
                )}
                {serviceName === 'outro' && details.observacoes && (
                  <div className="text-block-line" style={{ fontSize: '13px' }}>
                    <strong>Observações:</strong> {details.observacoes}
                  </div>
                )}
                {serviceFiles.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                    {serviceFiles.length} {serviceFiles.length === 1 ? 'documento anexo' : 'documentos anexos'}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {allFiles.length > 0 && (
          <div className="document-attachments" style={{ marginTop: '20px' }}>
            <div className="attachments-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span className="attachments-count">{allFiles.length} {allFiles.length === 1 ? 'anexo' : 'anexos'}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownloadAll(e);
                }}
                style={{
                  padding: '6px 12px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'background 0.2s',
                  zIndex: 10,
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
                disabled={allFiles.length === 0}
              >
                ⬇ Descarregar Todos ({allFiles.length})
              </button>
            </div>
            <div className="attachments-grid">
              {allFiles.map((file, index) => (
                <div 
                  key={index} 
                  className="attachment-icon-item"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    handleFileDownload(file, e);
                  }}
                  title="Clique para descarregar"
                >
                  <div className="attachment-icon-wrapper">
                    <div className={`attachment-icon-shape ${getFileIconClass(file.type || '', file.name || '')}`}>
                      <div className="file-icon-content"></div>
                    </div>
                    <div className="attachment-icon-label">
                      <div className="attachment-icon-envelope">✉</div>
                      <div className="attachment-icon-filename">{file.name || file.filename || file.file_name || 'Documento sem nome'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Flex spacer - fills remaining space; minHeight:1 prevents collapse at 75% zoom */}
        <div style={{ flex: '1 1 0%', minHeight: 1 }} aria-hidden="true" />
        </div>
      </div>
      )}

      {showNotesModal && (
        <div
          onClick={() => setShowNotesModal(false)}
          className="proposal-card-notes-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="proposal-card-notes-modal"
          >
            <div className="proposal-card-notes-modal-header">
              <h3>Notas do BackOffice</h3>
              <button
                type="button"
                onClick={() => setShowNotesModal(false)}
                className="proposal-card-notes-modal-close"
                aria-label="Fechar modal de notas"
              >
                ×
              </button>
            </div>
            <div className="proposal-card-notes-modal-body">
              {cardNotes.length === 0 ? (
                <div className="proposal-card-notes-empty">Ainda não existem notas para esta proposta.</div>
              ) : (
                cardNotes.map((note, idx) => {
                  const author = note.author_name || note.created_by_name || note.created_by || 'BackOffice';
                  const createdAt = note.created_at ? formatDateTime(note.created_at) : null;
                  return (
                    <div key={idx} className="proposal-card-note-item">
                      <div className="proposal-card-note-meta">
                        <strong>{author}</strong>
                        {createdAt ? ` - ${createdAt}` : ''}
                      </div>
                      <div className="proposal-card-note-content">
                        {note.content || '(Sem conteúdo)'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestCard;
